import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen, Search, Sparkles } from "lucide-react";
import { searchCatalogs } from "@/lib/catalogSearch";

const EXAMPLES = ["BOP anular", "container offshore", "chave de torque THL"];

const groupResultsByCatalog = (results) => {
  const catalogs = new Map();

  results.forEach((result) => {
    if (!catalogs.has(result.file)) {
      catalogs.set(result.file, {
        file: result.file,
        catalogTitle: result.catalogTitle,
        pages: [],
      });
    }

    const catalog = catalogs.get(result.file);
    if (!catalog.pages.includes(result.page)) catalog.pages.push(result.page);
  });

  return Array.from(catalogs.values()).map((catalog) => ({
    ...catalog,
    pages: catalog.pages.sort((a, b) => a - b),
  }));
};

export default function CatalogSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const catalogResults = groupResultsByCatalog(results);

  const runSearch = (term) => {
    const cleanTerm = term.trim();
    if (!cleanTerm) return;
    setQuery(cleanTerm);
    setResults(searchCatalogs(cleanTerm));
    setHasSearched(true);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    runSearch(query);
  };

  return (
    <div className="mt-10 overflow-hidden rounded-xl border border-brand/15 bg-white shadow-[0_22px_60px_rgba(15,61,46,0.10)]">
      <div className="border-b border-surface-line bg-[linear-gradient(120deg,#0f3d2e_0%,#15533f_100%)] px-6 py-6 text-white sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/12">
              <Sparkles className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">IA Wellpec</div>
              <h2 className="mt-1 font-display text-2xl font-bold">Busca inteligente nos catálogos</h2>
            </div>
          </div>
          <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white/90">
            6 documentos indexados
          </span>
        </div>
      </div>

      <div className="p-6 sm:p-8">
        <p className="max-w-4xl text-base leading-relaxed text-ink">
          Digite o que procura. Informe categoria, descrição do equipamento ou material, fabricante, modelo ou part number. Nossa IA irá indicar o catálogo adequado.
        </p>

        <form onSubmit={handleSubmit} className="mt-5" role="search">
          <label htmlFor="catalog-search" className="sr-only">Equipamento ou material procurado</label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-muted" aria-hidden="true" />
              <input
                id="catalog-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Ex.: BOP anular, container offshore ou modelo THL-8"
                className="h-14 w-full rounded-lg border border-surface-line bg-surface-alt pl-12 pr-4 text-base text-ink outline-none transition placeholder:text-ink-muted/70 focus:border-brand focus:bg-white focus:ring-4 focus:ring-brand/10"
              />
            </div>
            <button
              type="submit"
              disabled={!query.trim()}
              className="inline-flex h-14 items-center justify-center gap-2 rounded-lg bg-brand px-7 font-semibold text-white transition hover:bg-brand-mid disabled:cursor-not-allowed disabled:opacity-45"
            >
              Buscar nos catálogos
              <Search className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </form>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-ink-muted">Experimente:</span>
          {EXAMPLES.map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => runSearch(example)}
              className="rounded-full border border-surface-line bg-white px-3 py-1.5 font-medium text-brand transition hover:border-brand/40 hover:bg-brand/5"
            >
              {example}
            </button>
          ))}
        </div>

        <div className="mt-7" aria-live="polite">
          {hasSearched && results.length > 0 && (
            <div>
              <div className="flex items-center gap-2 pr-14 text-sm font-semibold text-brand sm:pr-0">
                <BookOpen className="h-4 w-4" aria-hidden="true" />
                Encontramos {catalogResults.length === 1 ? "um catálogo" : `${catalogResults.length} catálogos`} para sua busca
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                {catalogResults.map((catalog) => (
                  <article key={catalog.file} className="flex flex-col rounded-lg border border-surface-line bg-surface-alt p-5">
                    <div className="text-xs font-bold uppercase tracking-[0.14em] text-bronze">Catálogo</div>
                    <h3 className="mt-2 font-display text-xl font-bold text-ink">{catalog.catalogTitle}</h3>
                    <div className="mt-5 text-sm font-semibold text-ink">Páginas encontradas</div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {catalog.pages.map((page) => (
                        <a
                          key={page}
                          href={`${catalog.file}#page=${page}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-full border border-brand/20 bg-white px-3 py-2 text-sm font-semibold text-brand transition hover:border-brand/45 hover:bg-brand/5"
                        >
                          Página {page} <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {hasSearched && results.length === 0 && (
            <div className="rounded-lg border border-bronze/25 bg-[#fffaf3] p-6 sm:flex sm:items-center sm:justify-between sm:gap-6">
              <div>
                <h3 className="font-display text-xl font-bold text-ink">Não encontramos esse item nos catálogos atuais.</h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink-muted">
                  Abra uma solicitação de orçamento para que nosso time possa buscar no mercado uma solução adequada à sua necessidade.
                </p>
              </div>
              <Link
                to="/contato"
                className="mt-5 inline-flex shrink-0 items-center gap-2 rounded-full bg-brand px-6 py-3 font-semibold text-white hover:bg-brand-mid sm:mt-0"
              >
                Solicitar orçamento <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
